import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { toast } from 'react-hot-toast';

import {
    Button,
    Card,
    CardTitle,
    Spinner
} from '@/components/ui';
import {
    Input,
    Select,
    ProgressIndicator
} from '@/components/forms';
import { PageHeader } from '@/components/layout';
import { ROUTES } from '@/constants/routes';
import { useEquipmentList, useParts } from '@/api/useEquipment';
import { useCreatePartsRequisition, usePartsRequisitionDetail, useUpdatePartsRequisition } from '@/api/usePartsRequisitions';
import type { PartsRequisitionData } from '@/types/mmms';
import { useAuthStore } from '@/stores/useAuthStore';
import { getCurrentWeekRange } from '@/utils/dateUtils';

import styles from './PartsRequisition.module.css';

const STEPS = [
    { label: 'Cabecera' },
    { label: 'Repuestos' },
    { label: 'Entrega' },
    { label: 'Revisión' }
];

const PRIORITY_OPTIONS = [
    { value: 'low', label: 'Baja' },
    { value: 'medium', label: 'Media' },
    { value: 'high', label: 'Alta' },
    { value: 'urgent', label: 'Urgente' }
];

const COMMON_KITS = [
    {
        name: 'Kit 250h - Jumbo Sandvik', parts: [
            { part_code: '847293', description: 'Filtro Aceite Motor', quantity: 1 },
            { part_code: '847294', description: 'Filtro Combustible', quantity: 2 },
            { part_code: '847295', description: 'O-ring Sello', quantity: 4 }
        ]
    },
    {
        name: 'Kit Hidráulico Rápido', parts: [
            { part_code: 'H1029', description: 'Manguera 1/2" x 1m', quantity: 2 },
            { part_code: 'H1030', description: 'Acople Rápido', quantity: 2 }
        ]
    }
];

export function PartsRequisition() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;
    const { getUser } = useAuthStore();
    const currentUser = getUser();
    const currentWeek = getCurrentWeekRange();

    const [currentStep, setCurrentStep] = useState(0);

    const { data: equipmentList, isLoading: loadingEquipment } = useEquipmentList();
    const { data: partsList, isLoading: loadingParts } = useParts();
    const { data: requisitionDetail, isLoading: loadingDetail } = usePartsRequisitionDetail(id || '');
    const { mutate: createRequisition, isPending: creating } = useCreatePartsRequisition();
    const { mutate: updateRequisition, isPending: updating } = useUpdatePartsRequisition();

    const isSubmitting = creating || updating;

    const { control, handleSubmit, watch, reset, setValue: setFormValue } = useForm<PartsRequisitionData>({
        defaultValues: {
            requisition_number: `REQ-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`,
            date: new Date().toISOString().split('T')[0],
            equipment_id: 0,
            justification: '',
            priority: 'medium',
            status: 'draft',
            items: [],
            required_date: new Date(Date.now() + 86400000 * 2).toISOString().split('T')[0],
            delivery_location: 'Almacén Central - Nivel 0',
            contact_person: '',
            contact_phone: ''
        }
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "items"
    });

    useEffect(() => {
        if (isEdit && requisitionDetail) {
            reset(requisitionDetail);
        }
    }, [isEdit, requisitionDetail, reset]);

    const onSubmit = (data: PartsRequisitionData) => {
        if (!data.equipment_id) {
            toast.error('Por favor seleccione un equipo');
            return;
        }

        const payload = {
            ...data,
            status: 'submitted' as const,
            equipment: data.equipment_id,
            items_write: data.items
        };

        if (isEdit) {
            updateRequisition({ id: id!, data: payload as any }, {
                onSuccess: () => {
                    toast.success('Pedido de repuestos actualizado');
                    navigate(ROUTES.home);
                }
            });
        } else {
            createRequisition(payload as any, {
                onSuccess: () => {
                    toast.success('Pedido de repuestos enviado');
                    navigate(ROUTES.home);
                }
            });
        }
    };

    const nextStep = () => {
        setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
        window.scrollTo(0, 0);
    };

    const prevStep = () => {
        setCurrentStep(prev => Math.max(prev - 1, 0));
        window.scrollTo(0, 0);
    };

    const addItem = () => {
        append({
            part_code: '',
            description: '',
            quantity: 1
        });
    };

    if (loadingEquipment || loadingParts || (isEdit && loadingDetail)) {
        return <div className={styles.loading}><Spinner size="lg" /></div>;
    }

    return (
        <div className={styles.container}>
            <PageHeader
                title={isEdit ? "Editar Pedido de Repuestos" : "Nuevo Pedido de Repuestos"}
                subtitle={`Solicitud de materiales y repuestos para mantenimiento • ${currentWeek.label}`}
                backPath={ROUTES.home}
            />

            <ProgressIndicator steps={STEPS} currentStep={currentStep} />

            <form onSubmit={handleSubmit(onSubmit)}>
                {currentStep === 0 && (
                    <Card className={styles.stepCard} variant="full">
                        <CardTitle variant="simple">Cabecera del Pedido</CardTitle>
                        <div className={styles.formGrid}>
                            <Controller
                                name="requisition_number"
                                control={control}
                                render={({ field }) => (
                                    <Input name="req_num" label="N° Solicitud" isReadOnly value={field.value} setValue={field.onChange} />
                                )}
                            />
                            <Controller
                                name="date"
                                control={control}
                                render={({ field }) => (
                                    <Input name="date" label="Fecha" type="date" value={field.value} setValue={field.onChange} />
                                )}
                            />
                        </div>
                        <div className={styles.formGrid}>
                            <Controller
                                name="equipment_id"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        label="Equipo"
                                        value={field.value.toString()}
                                        onChange={(e) => field.onChange(parseInt(e.target.value))}
                                        options={equipmentList?.map(e => ({ value: e.id.toString(), label: e.code })) || []}
                                    />
                                )}
                            />
                            <Controller
                                name="priority"
                                control={control}
                                render={({ field }) => (
                                    <Select label="Prioridad" value={field.value} onChange={(e) => field.onChange(e.target.value)} options={PRIORITY_OPTIONS} />
                                )}
                            />
                        </div>
                        <Controller
                            name="justification"
                            control={control}
                            render={({ field }) => (
                                <Input name="justification" label="Justificación / Motivo" multiline rows={3} value={field.value} setValue={field.onChange} placeholder="Explique por qué se necesitan estos repuestos..." />
                            )}
                        />
                    </Card>
                )}

                {currentStep === 1 && (
                    <div className={styles.itemsList}>
                        <Card className={styles.itemsCard} variant="full">
                            <CardTitle variant="simple">Lista de Artículos</CardTitle>
                            {fields.length === 0 ? (
                                <p className={styles.emptyText}>No hay artículos agregados. Haga clic en el botón de abajo para agregar.</p>
                            ) : (
                                <div className={styles.itemsHeader}>
                                    <span>N° Parte / Código</span>
                                    <span>Descripción</span>
                                    <span>Cant.</span>
                                    <span></span>
                                </div>
                            )}

                            <div className={styles.kitSelector}>
                                <span className={styles.kitLabel}>¿Desea cargar un Kit Predefinido?</span>
                                <div className={styles.kitsGrid}>
                                    {COMMON_KITS.map(kit => (
                                        <button
                                            key={kit.name}
                                            type="button"
                                            className={styles.kitBtn}
                                            onClick={() => {
                                                kit.parts.forEach(p => append(p));
                                            }}
                                        >
                                            {kit.name}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {fields.map((item, index) => (
                                <div key={item.id} className={styles.itemRow}>
                                    <Controller
                                        name={`items.${index}.part_code`}
                                        control={control}
                                        render={({ field }) => (
                                            <div className={styles.partSearch}>
                                                <Input
                                                    name={`code-${index}`}
                                                    value={field.value}
                                                    setValue={(v) => {
                                                        field.onChange(v);
                                                        const part = partsList?.find(p => p.code === v);
                                                        if (part) {
                                                            setFormValue(`items.${index}.description`, part.description);
                                                        }
                                                    }}
                                                    placeholder="Código (847293...)"
                                                />
                                            </div>
                                        )}
                                    />
                                    <Controller
                                        name={`items.${index}.description`}
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                name={`desc-${index}`}
                                                value={field.value}
                                                setValue={field.onChange}
                                                placeholder="Descripción..."
                                            />
                                        )}
                                    />
                                    <Controller
                                        name={`items.${index}.quantity`}
                                        control={control}
                                        render={({ field }) => (
                                            <Input name={`qty-${index}`} type="number" value={field.value.toString()} setValue={(v) => field.onChange(parseInt(v) || 0)} />
                                        )}
                                    />
                                    <Button type="button" variant="secondary" size="sm" onClick={() => remove(index)} className={styles.removeBtn}>×</Button>
                                </div>
                            ))}

                            <Button type="button" variant="secondary" onClick={addItem} className={styles.addItemBtn}>
                                <i className="fa-solid fa-plus"></i> Agregar Artículo
                            </Button>
                        </Card>
                    </div>
                )}

                {currentStep === 2 && (
                    <Card className={styles.stepCard} variant="full">
                        <CardTitle variant="simple">Entrega y Contacto</CardTitle>
                        <div className={styles.formGrid}>
                            <Controller
                                name="required_date"
                                control={control}
                                render={({ field }) => (
                                    <Input name="req_date" label="Fecha Requerida" type="date" value={field.value || ''} setValue={field.onChange} placeholder="Ej: 29/01/2026" />
                                )}
                            />
                            <Controller
                                name="delivery_location"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        label="Lugar de Entrega"
                                        value={field.value || ''}
                                        onChange={(e) => field.onChange(e.target.value)}
                                        options={[
                                            { value: 'Almacén Central - Nivel 0', label: 'Almacén Central - Nivel 0' },
                                            { value: 'Taller Mecánico - Zona B', label: 'Taller Mecánico - Zona B' },
                                            { value: 'Frente de Trabajo - Rampa 1', label: 'Frente de Trabajo - Rampa 1' },
                                            { value: 'Oficina Planeamiento', label: 'Oficina Planeamiento' }
                                        ]}
                                    />
                                )}
                            />
                        </div>
                        <div className={styles.formGrid}>
                            <Controller
                                name="contact_person"
                                control={control}
                                render={({ field }) => (
                                    <Input name="contact" label="Persona de Contacto" placeholder="Nombre completo..." value={field.value || ''} setValue={field.onChange} />
                                )}
                            />
                            <Controller
                                name="contact_phone"
                                control={control}
                                render={({ field }) => (
                                    <Input name="phone" label="Teléfono / Anexo" placeholder="999..." value={field.value || ''} setValue={field.onChange} />
                                )}
                            />
                        </div>
                    </Card>
                )}

                {currentStep === 3 && (
                    <Card className={styles.stepCard} variant="full">
                        <CardTitle variant="simple">Resumen del Pedido</CardTitle>
                        <div className={styles.summaryBox}>
                            <p><strong>Solicitante:</strong> {currentUser?.username}</p>
                            <p><strong>Equipo:</strong> {equipmentList?.find(e => e.id === watch("equipment_id"))?.code || 'No seleccionado'}</p>
                            <p><strong>Prioridad:</strong> {watch("priority").toUpperCase()}</p>
                            <p><strong>Fecha Req:</strong> {watch("required_date")}</p>
                            <p><strong>Lugar:</strong> {watch("delivery_location")}</p>
                            <div className={styles.divider}></div>
                            <p><strong>Artículos:</strong> {fields.length}</p>
                        </div>
                        <div className={styles.verificationBox}>
                            <p className={styles.warningText}>
                                Esta solicitud será enviada al departamento de logística. Verifique que los códigos de parte sean correctos para evitar retrasos.
                            </p>
                        </div>
                    </Card>
                )}

                <div className={styles.actions}>
                    {currentStep > 0 && <Button type="button" variant="secondary" onClick={prevStep}>Anterior</Button>}
                    <span className={styles.spacer}></span>
                    {currentStep < STEPS.length - 1 ? (
                        <Button type="button" onClick={nextStep}>Continuar</Button>
                    ) : (
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? <Spinner variant="secondary" size="sm" /> : (isEdit ? 'Actualizar Requerimiento' : 'Enviar Requerimiento')}
                        </Button>
                    )}
                </div>
            </form>
        </div>
    );
}
